import { Control, Controller } from "react-hook-form";
import AccessibleCheckbox from "../../../../../../components/global_components/AccessibleCheckbox";
import { ChannelType } from "../enums/ChannelType";
import { Key } from "react";
import { ChannelIcon } from "../pages/notifications/components/NotifyNotificationsChannelIcon";
import { Radio } from "semantic-ui-react";
import Tooltip from "../../../../../../components/Tooltip";
import { NotifyNotificationsFormDataChannels } from "./NotifyNotificationsForm";

export function ControlledNotifyNotificationsChannels({
  control,
  name,
  readonly,
  label,
}: {
  control: Control;
  name: string;
  readonly?: boolean;
  label?: string;
}) {
  return (
    <Controller
      name={name}
      control={control}
      render={({ onChange, value }) => (
        <NotifyNotificationsChannels value={value} onChange={onChange} readonly={readonly} label={label} />
      )}
    />
  );
}

export function NotifyNotificationsChannels({
  value,
  onChange = () => {},
  readonly,
  label,
  className,
}: {
  value: NotifyNotificationsFormDataChannels;
  onChange?: (value: NotifyNotificationsFormDataChannels) => void;
  readonly?: boolean;
  label?: string;
  className?: string;
}) {
  const activeChannels = value?.active ?? [];
  const voiceForceSendAll = value?.additionalData?.voiceForceSendAll ?? false;

  const valueSet = new Set<ChannelType>(activeChannels);
  const valueSetHasAll = valueSet.size === Object.keys(ChannelType).length;
  const valueSetHasSome = valueSet.size > 0 && valueSet.size < Object.keys(ChannelType).length;

  const setVoiceForceSendAll = (forceSendAll: boolean) => {
    onChange({
      active: activeChannels,
      additionalData: {
        voiceForceSendAll: forceSendAll,
      },
    });
  };

  const toggle = (channel: ChannelType) => {
    const newValue = new Set(valueSet);
    if (newValue.has(channel)) {
      newValue.delete(channel);
    } else {
      newValue.add(channel);
    }
    onChange({
      active: Array.from(newValue),
      additionalData: {
        voiceForceSendAll,
      },
    });
  };

  const toggleAll = () => {
    const allChannels = Object.values(ChannelType) as ChannelType[];
    onChange({
      active: valueSetHasAll ? [] : allChannels,
      additionalData: {
        voiceForceSendAll,
      },
    });
  };

  if (readonly) {
    return (
      <>
        {label && <label>{label}</label>}
        <div>
          {valueSetHasAll && <div className='tw-text-text-secondary'>All Channels</div>}
          {!valueSetHasAll && valueSetHasSome && (
            <div className='tw-flex tw-gap-2'>
              {Array.from(valueSet).map((channel, index) => {
                return (
                  <div key={channel as Key}>
                    <div className='tw-flex tw-gap-1'>
                      <ChannelIcon channel={channel} />
                      <span className='tw-text-text-secondary'>{channel}</span>
                    </div>
                    {index < valueSet.size - 1 && <span className='tw-text-text-secondary'>•</span>}
                  </div>
                );
              })}
            </div>
          )}
          {!valueSetHasSome && <div className='tw-text-text-secondary'>No Channels Selected</div>}
        </div>
      </>
    );
  }

  return (
    <>
      {label && <label>{label}</label>}
      <div className={className}>
        <AccessibleCheckbox
          label='All Channels'
          onChange={toggleAll}
          checked={valueSetHasAll}
          indeterminate={valueSetHasSome}
        />
        <AccessibleCheckbox
          label='Push Notifications to iOS and Android'
          onChange={() => toggle(ChannelType.Push)}
          checked={valueSet.has(ChannelType.Push)}
        />
        <AccessibleCheckbox
          label='Email Subscribers'
          onChange={() => toggle(ChannelType.Email)}
          checked={valueSet.has(ChannelType.Email)}
        />
        <AccessibleCheckbox
          label='SMS'
          onChange={() => toggle(ChannelType.Sms)}
          checked={valueSet.has(ChannelType.Sms)}
        />
        <AccessibleCheckbox
          label='Voice'
          onChange={() => toggle(ChannelType.Voice)}
          checked={valueSet.has(ChannelType.Voice)}
        />
        {valueSet.has(ChannelType.Voice) && (
          <div className='tw-ml-6 tw-flex tw-flex-col'>
            <div className='tw-flex tw-items-center'>
              <Radio
                label={valueSet.has(ChannelType.Sms) ? "Landlines and SMS Failures" : "Landlines Only"}
                checked={voiceForceSendAll === false}
                onChange={() => setVoiceForceSendAll(false)}
              />
              <Tooltip>Numbers are considered landlines if they are not capable of receiving SMS</Tooltip>
            </div>
            <Radio
              label='All Numbers'
              checked={voiceForceSendAll === true}
              onChange={() => setVoiceForceSendAll(true)}
            />
          </div>
        )}
        <AccessibleCheckbox
          label='Twitter'
          onChange={() => toggle(ChannelType.Twitter)}
          checked={valueSet.has(ChannelType.Twitter)}
        />
        <AccessibleCheckbox
          label='Instagram'
          onChange={() => toggle(ChannelType.Instagram)}
          checked={valueSet.has(ChannelType.Instagram)}
        />
      </div>
    </>
  );
}
